/*    
<summary>
  This functional component "AddStoreManagementForm" provides form to add new user or update existing user.
  Developer:Aashish Singh, Created Date:29-March-2023
</summary>
<returns>Returns JSX</returns>
*/

import { Field, Form, Formik, FormikProps, useFormikContext } from "formik";
import { cloneDeep } from "lodash";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import {
  alphaNumericCharcaterRegex,
  integerNumJpRegex,
  integerRegex,
  inValidCharacterForStoreName,
  stringJpRegex,
} from "../../../../constants/constants";
import { ActionType } from "../../../../constants/enums/action-type-enum";
import { ModuleType } from "../../../../constants/enums/module-type-enum";
import userTypeEnum from "../../../../constants/enums/user-type-enum";
import { useStore } from "../../../../contexts/store-provider";
import { isOperationPermittedRoleBase } from "../../../../helpers/action-permission-helper";
import { getUserTypeFromLocalStorage } from "../../../../helpers/localstorage-helper";
import { toHalfWidth } from "../../../../helpers/validate-helper";
import { IOption } from "../../../../models/ICommon";
import LoaderButton from "../../../../shared-components/button/loader-button";
import FormikFormImagePicker from "../../../../shared-components/FormikFormHandlers/FormikFormImagePicker";
import FormikFormInput from "../../../../shared-components/FormikFormHandlers/FormikFormInput";
import FormikFormInputAddress from "../../../../shared-components/FormikFormHandlers/FormikFormInputAddress";
import FormikFormInputForRegexCheck from "../../../../shared-components/FormikFormHandlers/FormikFormInputForRegexCheck";
import FormikFormInputMobileNumber from "../../../../shared-components/FormikFormHandlers/FormikFormInputMobileNumber";
import FormikFormSelect from "../../../../shared-components/FormikFormHandlers/FormikFormSelect";
import {
  AddStoreValidationSchema,
  EditStoreValidationSchema,
} from "../../../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormLoader from "../../../../shared-components/FormLoader/FormLoader";
import Label from "../../../../shared-components/label/label";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import { formatMessage } from "../../../../translations/format-message";
import "./add-edit-store-form.css";
import FormikFormPasswordInput from "../../../../shared-components/FormikFormHandlers/FormikFormPasswordInput";
import { FormObserver } from "../../../../shared-components/address/form-observer";

interface IProps {
  id: number;
  submitHandler: (data: any) => void;
  onModalClosed: () => void;
  getStoreManagementLoading: boolean;
  addUpdateStoreManagementLoading: boolean;
  initialValues: any;
  businessTypeOptions: IOption[];
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
  handleDeleteSubmitHandler: () => void;
  onDeleteModalClosed: () => void;
  viewOnly: boolean;
}

const AddStoreManagementForm = (props: IProps) => {
  const formikRef = useRef<FormikProps<any>>(null);
  const {
    showDeletePopup,
    deletePopupToggleHandler,
    handleDeleteSubmitHandler,
    onDeleteModalClosed,
    viewOnly,
  } = props;
  const { storeManagementStore } = useStore();
  const { GetPrefectureUsingPostalCode, prefectureByZipCode } =
    storeManagementStore;
  const [postalCode, setPostalCode] = useState<string>("");

  useEffect(() => {
    if (postalCode && postalCode?.length == 7) {
      GetPrefectureUsingPostalCode(postalCode);
    }
  }, [postalCode]);

  const handleDelete = () => {
    deletePopupToggleHandler();
  };

  const handleDeleteModalClosed = () => {
    deletePopupToggleHandler();
  };

  return (
    <>
      {showDeletePopup && (
        <DeletePopup
          modalSubmit={handleDeleteSubmitHandler}
          modalClosed={handleDeleteModalClosed}
          isLoading={false}
          title={formatMessage("delete_confirmation")}
          message={formatMessage("are_you_sure_you_want_to_delete_this_store?")}
        />
      )}
      <Modal show={true} centered>
        <Modal.Header>
          <Modal.Title>
            {props.id === -1 ? (
              <FormattedMessage id="store_registration" />
            ) : (
              <FormattedMessage id="store_details" />
            )}
            <button
              onClick={props.onModalClosed}
              disabled={props.addUpdateStoreManagementLoading}
              className="Crossicons"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="23"
                height="23"
                fill="currentColor"
                className="bi bi-x"
                viewBox="0 0 16 16"
              >
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
              </svg>
            </button>
          </Modal.Title>
        </Modal.Header>
        <Formik
          innerRef={formikRef}
          initialValues={props.initialValues}
          enableReinitialize={true}
          validationSchema={
            props.id > 0 ? EditStoreValidationSchema : AddStoreValidationSchema
          }
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let tempData = cloneDeep(values);
            tempData.PostalCode = toHalfWidth(values.PostalCode);
            tempData.PrefectureName = values.PrefectureName;
            tempData.MobileNumber = toHalfWidth(values.MobileNumber);
            tempData.Address = values.Address?.replace(
              values.PrefectureName,
              ""
            );
            tempData.Email = values.Email?.toLowerCase();
            tempData.StoreLink = props.id > 0 ? tempData?.StoreLink : `${process.env.REACT_APP_BASE_URL_STORE}${tempData.StoreLink}`
            props.submitHandler(tempData);
          }}
        >
          {({ setFieldValue, values }) => {
            return (
              <Form className="user-form">
                {props.getStoreManagementLoading ? (
                  <FormLoader loading={props.getStoreManagementLoading} />
                ) : null}
                <FormObserver
                  prefectureByZipCode={prefectureByZipCode}
                  initialPostalCode={props.initialValues.PostalCode}
                />
                <Modal.Body>
                  {/* Row 1 */}
                  {/* {process.env.REACT_APP_BASE_URL_STORE} */}
                  <div className="form-group mb-3 row">
                    <div className="col-sm-4 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="store_name"
                      />
                      <Field
                        className="form-control"
                        name="StoreName"
                        type="text"
                        placeholder="enter_store_name"
                        as={FormikFormInput}
                        // invalidcharacter={inValidCharacterForStoreName}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-4 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="store_name_kana"
                      />
                      <Field
                        className="form-control"
                        name="KanaName"
                        type="text"
                        placeholder="enter_store_name_kana"
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-4 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="store_owner_name"
                      />
                      <Field
                        className="form-control"
                        name="StoreOwnerName"
                        type="text"
                        placeholder="enter_store_owner_name"
                        as={FormikFormInput}
                        // invalidcharacter={inValidCharacterForStoreName}
                        disabled={viewOnly}
                      />
                    </div>

                    {/* <div className="col-sm-4 mb-3">
                        <Label
                          className="label-style"
                          required={false}
                          label="prefecture_name"
                        />
                        <Field
                          className="form-control"
                          name="PrefectureName"
                          type="text"
                          placeholder="prefecture_name"
                          as={FormikFormInput}
                          disabled={true}
                        />
                      </div> */}

                    {
                      <div className="col-sm-4 mb-3">
                        <Label
                          className="label-style"
                          required={true}
                          label="store_code"
                        />
                        <Field
                          className="form-control"
                          name="StoreCode"
                          type="text"
                          placeholder="enter_store_code"
                          as={FormikFormInput}
                          // regex={alphaNumericCharcaterRegex}
                          disabled={props.id > 0 ? true : false}
                        />
                      </div>
                    }

                    {getUserTypeFromLocalStorage() !==
                      userTypeEnum[userTypeEnum.HeadQuarter] && (
                      <div
                        className={`mb-3 ${
                          props.id > -1 ? "col-sm-4 " : "col-sm-6 "
                        }`}
                      >
                        <Label
                          className="label-style"
                          required={true}
                          label="business_type"
                        />
                        <Field
                          name="BusinessTypeId"
                          as={FormikFormSelect}
                          options={props.businessTypeOptions}
                          disabled={
                            getUserTypeFromLocalStorage() ===
                            userTypeEnum[userTypeEnum.HeadQuarter]
                              ? true
                              : viewOnly
                          }
                        />
                      </div>
                    )}
                    <div
                      className={`mb-3 ${
                        props.id > -1 ? "col-sm-4 " : "col-sm-6 "
                      }`}
                    >
                      <Label
                        className="label-style"
                        required={false}
                        label="store_category"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            type="radio"
                            name="StoreCategory"
                            value="0"
                            id="fcStore"
                            className="form-check-input"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="fcStore">
                            <FormattedMessage id="fc_store" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            type="radio"
                            name="StoreCategory"
                            value="1"
                            id="dmStore"
                            className="form-check-input"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="dmStore">
                            <FormattedMessage id="dm_store" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="email"
                      />
                      <Field
                        className="form-control"
                        name="Email"
                        type="email"
                        autoComplete="new-email"
                        placeholder="enter_email"
                        validcharacter={["@", "#", "$", "-", "_", "."]}
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="postal_code"
                      />
                      <Field
                        className="form-control"
                        name="PostalCode"
                        type="text"
                        autoComplete="new-postal-code"
                        placeholder="enter_post_code"
                        regex={integerNumJpRegex}
                        setState={setPostalCode}
                        as={FormikFormInputForRegexCheck}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-12 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="address"
                      />
                      <Field
                        className="form-control"
                        name="Address"
                        type="text"
                        placeholder="enter_address"
                        autoComplete="new-address"
                        prefectureName={values.PrefectureName}
                        validcharacter={[
                          "@",
                          "#",
                          "$",
                          "-",
                          "_",
                          ".",
                          "/",
                          ",",
                          "%",
                          "!",
                          "&",
                        ]}
                        as={FormikFormInputAddress}
                        disabled={viewOnly}
                      />
                    </div>
                    <div className="col-sm-4 mb-3">
                      <Label
                        className="label-style"
                        required={true}
                        label="contact_number"
                      />
                      <Field
                        className="form-control"
                        name="MobileNumber"
                        type="text"
                        autoComplete="new-mobile-number"
                        placeholder="enter_mobile_number"
                        regex={integerRegex}
                        as={FormikFormInputMobileNumber}
                        disabled={viewOnly}
                      />
                    </div>

                    <div className="col-sm-8 mb-3">
                      <Label
                        className="label-style"
                        required={false}
                        label="map_url"
                      />
                      <Field
                        className="form-control"
                        name="Url"
                        type="text"
                        validcharacter={[
                          "@",
                          "#",
                          "$",
                          "-",
                          "_",
                          ".",
                          "/",
                          ",",
                          "%",
                          "!",
                          "&",
                          "=",
                          "+",
                          "?",
                          ":",
                        ]}
                        // invalidcharacter={[" "]}
                        placeholder="enter_map_url"
                        as={FormikFormInput}
                        disabled={viewOnly}
                      />
                    </div>
                    {
                      props.id < 0 ? (
                        <>
                    <div className="col-sm-12 mb-3">
                      <Label
                        className="label-style"
                        required={false}
                        label="reservation_site"
                      />
                      <div className="input-group">
                        <span className="form-control">{process.env.REACT_APP_BASE_URL_STORE}</span>
                      {props.id > 0 && <Field
                        className="form-control"
                        name="StoreLink"
                        validcharacter={[
                          "-",
                          "_",
                          ":",
                          "#",
                          "@",
                          ".",
                          "/",
                          "?",
                          "=",
                          "%",
                        ]}
                        invalidcharacter={[" "]}
                        type="text"
                        placeholder="enter_reservation_site"
                        as={FormikFormInput}
                        autoComplete="new-store-link"
                        disabled={true}
                      />}
                      </div>
                    </div>
                        </>
                      ) 
                      : 
                      <>
                                          <div className="col-sm-12 mb-3">
                      <Label
                        className="label-style"
                        required={false}
                        label="reservation_site"
                      />
                      <div className="input-group">
                      <Field
                        className="form-control"
                        name="StoreLink"
                        validcharacter={[
                          "-",
                          "_",
                          ":",
                          "#",
                          "@",
                          ".",
                          "/",
                          "?",
                          "=",
                          "%",
                        ]}
                        invalidcharacter={[" "]}
                        type="text"
                        placeholder="enter_reservation_site"
                        as={FormikFormInput}
                        autoComplete="new-store-link"
                        disabled={true}
                      />
                      </div>
                    </div>
                      </>
                    }
                    {props.id < 0 && (
                      <>
                        <div className="col-sm-6 mb-3">
                          <Label
                            className="label-style"
                            required={true}
                            label="password"
                          />
                          <div className="form-group">
                            <Field
                              className="form-control"
                              name="Password"
                              autoComplete="new-password"
                              placeholder="enter_new_password"
                              invalidcharacter={[" "]}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                              as={FormikFormPasswordInput}
                            />
                          </div>
                        </div>
                        <div className="col-sm-6 mb-3 form-group">
                          <Label
                            className="label-style"
                            required={true}
                            label="confirm_password"
                          />
                          <div className="form-group">
                            <Field
                              className="form-control"
                              autocomplete="new-password"
                              name="ConfirmPassword"
                              placeholder="enter_confirm_password"
                              as={FormikFormPasswordInput}
                              invalidcharacter={[" "]}
                              validcharacter={[
                                "~",
                                "`",
                                "!",
                                "@",
                                "#",
                                "$",
                                "%",
                                "^",
                                "&",
                                "*",
                                "(",
                                ")",
                                "_",
                                "-",
                                "+",
                                "=",
                                "{",
                                "[",
                                "}",
                                "]",
                                "|",
                                "\\",
                                ":",
                                ";",
                                "'",
                                '"',
                                "<",
                                ",",
                                ">",
                                ".",
                                "?",
                                "/",
                              ]}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={false}
                        label="status"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="1"
                            id="valid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="valid">
                            <FormattedMessage id="valid" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            className="form-check-input"
                            name="Status"
                            type="radio"
                            value="0"
                            id="invalid"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="invalid">
                            <FormattedMessage id="invalid" />
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="col-sm-6 mb-3">
                      <Label
                        className="label-style"
                        required={false}
                        label="nomination_fee"
                      />
                      <div className="row">
                        <div className="col-sm-6">
                          <Field
                            type="radio"
                            name="IsNominationFee"
                            value="1"
                            id="include"
                            className="form-check-input"
                            disabled={viewOnly}
                          />
                          <label className="form-check-label" htmlFor="include">
                            <FormattedMessage id="include" />
                          </label>
                        </div>
                        <div className="col-sm-6">
                          <Field
                            type="radio"
                            name="IsNominationFee"
                            value="0"
                            id="notInclude"
                            className="form-check-input"
                            disabled={viewOnly}
                          />
                          <label
                            className="form-check-label"
                            htmlFor="notInclude"
                          >
                            <FormattedMessage id="not_include" />
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-4 mb-3">
                      <Label
                        className="label-style d-block pb-0"
                        required={false}
                        label="upload_image"
                      />
                      <Field
                        className="form-control"
                        name="Image"
                        type="text"
                        as={FormikFormImagePicker}
                        disabled={viewOnly}
                      />
                    </div>
                  </div>
                </Modal.Body>
                {/* Footer Buttons */}
                <div className="modal-footer center-content">
                  <Button
                    variant="outline-danger"
                    type="button"
                    onClick={props.onModalClosed}
                    disabled={props.addUpdateStoreManagementLoading}
                  >
                    <i className="bi bi-x-lg me-1"></i>
                    <FormattedMessage id="close" />
                  </Button>
                  {isOperationPermittedRoleBase(
                    ModuleType.StoreManagement,
                    ActionType.Edit
                  ) && (
                    <LoaderButton
                      type="submit"
                      iconName="bi-check-lg"
                      isLoading={props.addUpdateStoreManagementLoading}
                      text={props.id > -1 ? "button_update" : "button_register"}
                    />
                  )}
                  {props.id > -1 &&
                    isOperationPermittedRoleBase(
                      ModuleType.StoreManagement,
                      ActionType.Delete
                    ) && (
                      <Button
                        variant="danger"
                        type="button"
                        onClick={handleDelete}
                        disabled={props.addUpdateStoreManagementLoading}
                      >
                        <i className="bi bi-trash3-fill me-1"></i>
                        <FormattedMessage id="button_delete" />
                      </Button>
                    )}
                </div>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default DeletePopupHOC(observer(AddStoreManagementForm));
